import { Copy, ExternalLink, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { hashWalk } from '../../pages/archive/rulesExample';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { copyText } from '../../shared/lib/clipboard';

/**
 * 三张区块锚定卡（哈希高亮 + Etherscan/Blockchair/复制）+「为什么是这三个块」
 * 说明 + Etherscan 帮助弹层，一起抽出来自成一体。
 *
 * <p>房间三玩法（归档明细页）和 Milhar 验算页要展示同一批锚定区块，弹层状态
 * 若留给调用方各自 useState + 拼弹层 JSX，就是两份重复的接线。挪进来后
 * 两个调用方都是白拿。
 */
export function RoundAnchorCards({ anchors, roundCloseTs }: {
  anchors: { number?: number | null; hash?: string | null; timestamp?: number | null }[];
  roundCloseTs: number;
}) {
  const t = useI18nStore((state) => state.t);
  const [etherscanBlock, setEtherscanBlock] = useState<number | null>(null);
  const [copyFeedback, setCopyFeedback] = useState('');

  return (
    <>
      <AnchorWhyPanel anchors={anchors} roundCloseTs={roundCloseTs} />
      {anchors.filter((anchor) => anchor.hash).map((anchor, index) => {
        const walk = hashWalk(String(anchor.hash));
        const blockNumber = Number(anchor.number || 0);
        const timestamp = Number(anchor.timestamp || 0);
        const delta = timestamp && roundCloseTs ? Math.max(0, timestamp - roundCloseTs) : 0;
        const hash = String(anchor.hash || '');
        return (
          <div className={`rules-block-card rules-block-${index + 1}`} key={`walk-${anchor.number || index}`}>
            <div className="rules-block-card-header">
              <span className={`rules-block-num rules-block-num-${index + 1}`}>{index + 1}</span>
              <div className="rules-block-card-meta">
                <span className="rules-block-card-title">{t('rulesBlockN').replace('%n%', String(index + 1))}</span>
                <span className="rules-block-card-sub">
                  <span className="rules-block-idline">
                    #{anchor.number || '-'}
                    {delta ? <span className="rules-block-delta">+{delta}s</span> : null}
                  </span>
                  {timestamp ? (
                    <span className="rules-block-time">
                      <span className="rules-block-time-col">
                        <span className="rules-block-time-tag rules-block-time-utc">UTC</span>{formatUtcTime(timestamp)}
                      </span>
                      <span className="rules-block-time-col">
                        <span className="rules-block-time-tag rules-block-time-local">{t('localTimeTag')}</span>{formatAnchorTime(timestamp)}
                      </span>
                    </span>
                  ) : null}
                </span>
              </div>
              {blockNumber ? (
                <button className="rules-block-etherscan tap" type="button" aria-label={t('verifyEtherscan')} onClick={() => setEtherscanBlock(blockNumber)}>
                  <ExternalLink size={14} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <div className="rules-block-hash-label">{t('rulesPart1HashLabel')}</div>
            <div className="rules-block-hash">
              0x
              {walk.chars.map((ch, ci) => (
                <span key={ci} className={ch.isDigit ? (ch.inLast10 ? 'rules-hash-last10' : 'rules-hash-digit') : 'rules-hash-letter'}>{ch.c}</span>
              ))}
            </div>
            <div className="rules-block-hash-hint">{t('rulesPart1HashHint')}</div>
            <div className="rules-block-extract-label">{t('rulesPart1ExtractLabel')}</div>
            <div className="rules-digit-row">
              {walk.strip.split('').map((digit, di) => (
                <span key={di} className={`rules-digit-box rules-digit-box-${index + 1}`}>{digit}</span>
              ))}
            </div>
            <div className="rules-block-actions">
              <button type="button" disabled={!blockNumber} onClick={() => blockNumber ? setEtherscanBlock(blockNumber) : undefined}>
                <ExternalLink size={12} aria-hidden="true" />
                Etherscan
              </button>
              <a href={blockNumber ? `https://blockchair.com/ethereum/block/${blockNumber}` : undefined} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={12} aria-hidden="true" />
                Blockchair
              </a>
              <button
                type="button"
                disabled={!hash}
                onClick={async () => {
                  const ok = await copyText(hash);
                  setCopyFeedback(t(ok ? 'copySucceeded' : 'copyFailed'));
                  window.setTimeout(() => setCopyFeedback(''), 1800);
                }}
              >
                <Copy size={12} aria-hidden="true" />
                {t('copy')}
              </button>
            </div>
          </div>
        );
      })}
      {copyFeedback ? <p className="form-success draw-anchor-copy-feedback-react">{copyFeedback}</p> : null}

      {etherscanBlock ? (
        <EtherscanHelpSheet
          blockNumber={etherscanBlock}
          feedback={copyFeedback}
          onCopy={async () => {
            const ok = await copyText(etherscanUrl(etherscanBlock));
            setCopyFeedback(t(ok ? 'etherscanCopyOk' : 'etherscanCopyFail'));
            window.setTimeout(() => setCopyFeedback(''), 1800);
          }}
          onOpen={() => {
            window.open(etherscanUrl(etherscanBlock), '_blank', 'noopener,noreferrer');
            setEtherscanBlock(null);
          }}
          onClose={() => {
            setCopyFeedback('');
            setEtherscanBlock(null);
          }}
        />
      ) : null}
    </>
  );
}

function AnchorWhyPanel({
  anchors,
  roundCloseTs,
}: {
  anchors: { number?: number | null; hash?: string | null; timestamp?: number | null }[];
  roundCloseTs: number;
}) {
  const t = useI18nStore((state) => state.t);
  if (!anchors.length) return null;
  const visibleAnchors = anchors.slice(0, 3);
  return (
    <section className="draw-proof-card-react draw-anchor-why-card-react" data-draw-anchor-why-react="true">
      <span>
        <ShieldCheck size={14} aria-hidden="true" />
        {t('drawAnchorWhyTitle')}
      </span>
      <p>{t('drawAnchorWhyBody').replace('%time%', `${formatUtcTime(roundCloseTs)} UTC · ${formatAnchorTime(roundCloseTs)} ${t('localTimeTag')}`)}</p>
      <div className="draw-anchor-why-grid-react">
        {visibleAnchors.map((anchor, index) => {
          const blockNumber = Number(anchor.number || 0);
          const timestamp = Number(anchor.timestamp || 0);
          const delta = timestamp && roundCloseTs ? Math.max(0, timestamp - roundCloseTs) : 0;
          return (
            <div key={`${blockNumber || index}-${timestamp || 'pending'}`}>
              <b>B{index + 1}</b>
              <strong>#{blockNumber || '-'}</strong>
              <span>{timestamp ? (<>{formatUtcTime(timestamp)} UTC<br />{formatAnchorTime(timestamp)} {t('localTimeTag')}</>) : '-'}</span>
              <em>{delta ? `+${delta}s` : '-'}</em>
            </div>
          );
        })}
      </div>
      <div className="draw-anchor-reasons-react">
        <article>
          <strong>{t('drawAnchorReasonLockedTitle')}</strong>
          <p>{t('drawAnchorReasonLockedBody')}</p>
        </article>
        <article>
          <strong>{t('drawAnchorReasonPublicTitle')}</strong>
          <p>{t('drawAnchorReasonPublicBody')}</p>
        </article>
      </div>
    </section>
  );
}

export function EtherscanHelpSheet({
  blockNumber,
  feedback,
  onCopy,
  onOpen,
  onClose,
}: {
  blockNumber: number;
  feedback: string;
  onCopy: () => void;
  onOpen: () => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const url = etherscanUrl(blockNumber);

  return (
    <div className="sheet-backdrop nested-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet etherscan-help-sheet-react" role="dialog" aria-modal="true" aria-label={t('etherscanHelpTitle').replace('%n%', String(blockNumber))} onMouseDown={(event) => event.stopPropagation()} data-etherscan-help-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('etherscanHelpTitle').replace('%n%', String(blockNumber))}</h2>
            <p>{t('etherscanHelpBody')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="etherscan-url-row-react">
          <span>{t('etherscanHelpUrlLabel')}</span>
          <code>{url}</code>
        </div>
        <div className="etherscan-help-actions-react">
          <button className="secondary-button" type="button" onClick={onCopy}>
            <Copy size={15} aria-hidden="true" />
            {t('etherscanHelpCopy')}
          </button>
          <button className="primary-button" type="button" onClick={onOpen}>
            <ExternalLink size={15} aria-hidden="true" />
            {t('etherscanHelpOpen')}
          </button>
        </div>
        {feedback ? <p className="form-success etherscan-copy-feedback-react">{feedback}</p> : null}
        <p className="etherscan-help-tip-react">{t('etherscanHelpTip')}</p>
        <button className="link-button" type="button" onClick={onClose}>{t('etherscanHelpClose')}</button>
      </section>
    </div>
  );
}

export function etherscanUrl(blockNumber: number) {
  return `https://etherscan.io/block/${blockNumber}`;
}

function formatAnchorTime(seconds: number) {
  try {
    // hour12:false, like every other clock in the app and like the UTC line
    // printed right beside this one. Without it an en-US device rendered
    // "05:55:11 PM" next to "09:55:11" — an AM/PM clock no Brazilian uses,
    // and two different formats for the same instant.
    return new Date(seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch {
    return '--';
  }
}

function formatUtcTime(seconds: number) {
  if (!seconds) return '--:--';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    }).format(new Date(seconds * 1000));
  } catch {
    return '--:--';
  }
}
